import React, { useMemo } from "react"
import { useLingui } from "@lingui/react"
import { AirParams, Amount } from "@ssc-booking/api-contracts"
import * as O from "fp-ts/Option"
import { Trans } from "@lingui/macro"
import { validVsCodeValue } from "../../app-core/v3/_common/vs-code"
import { useECommerceMachine } from "../../app-core/_common/machines/machines-context"
import { useReceiptReadModel } from "../../app-core/v3/_common/machines/receipt-machine"
import {
    maybeSelectedSuiteCategory,
    useSuiteCategoriesReadModel,
} from "../../app-core/v3/_common/machines/suite-categories-machine"
import { fareCardByKey } from "../../app-core/v3/_common/read-models/fare"
import { gatewayNameByCode } from "../../app-core/v3/_common/read-models/gateway"
import { isValidPymentTerm, useDeposit } from "../Sidebar/useDeposit"
import { formatDate } from "../../app-core/_common/format/date"
import { buildReceiptSections as buildReceiptSections, SelectedFareForReceipt } from "./helpers/buildReceiptSections"
import styles from "./Receipt.module.scss"
import ReceiptSection from "./ReceiptSection"
import ReceiptRow from "./ReceiptRow"
import { formatPromotionalAmount } from "./helpers/utils"

type Props = {
    withInclusions?: boolean
}

const Receipt: React.FC<React.PropsWithChildren<Props>> = ({ withInclusions = false }) => {
    const { _ } = useLingui()
    const [state] = useECommerceMachine()
    const [stateSCRM] = useSuiteCategoriesReadModel(state)
    const [stateRC] = useReceiptReadModel(state)

    const vsCode = state.context.vsCode
    const { inclusionsContent, cardPromoBadgesContent } = state.context.sanityContent

    const selectedFare = stateRC.context.selectedFare?.key
        ? fareCardByKey(state.context, stateRC.context.selectedFare.key)
        : undefined
    const gatewayCode = selectedFare ? AirParams.gatewayOrUndefined(selectedFare.air) : undefined
    const selectedGateway = selectedFare ? gatewayNameByCode(selectedFare.options, gatewayCode) : undefined

    const selectedSuiteCategory = maybeSelectedSuiteCategory(stateSCRM)
    const suiteNumber = state.context.selectedSuiteNumber
    const price = stateRC.context.receipt?.price
    const vsCodeNumber = vsCode ? validVsCodeValue(vsCode) : undefined

    const summary = useMemo(() => {
        if (!price || !price.paxPrices) return []

        const selectedFareForPriceSummary: SelectedFareForReceipt | undefined =
            selectedFare && selectedFare.status === "good"
                ? {
                      fareName: selectedFare.fareLabel,
                      inclusionsContent: inclusionsContent,
                      inclusions: selectedFare.response.price.inclusions.map((i) => i.inclusionCode),
                  }
                : undefined

        return buildReceiptSections(
            _,
            withInclusions,
            price,
            selectedSuiteCategory,
            suiteNumber,
            cardPromoBadgesContent,
            selectedFareForPriceSummary,
            selectedGateway,
            vsCodeNumber,
        )
    }, [
        price,
        selectedFare,
        inclusionsContent,
        _,
        withInclusions,
        selectedSuiteCategory,
        suiteNumber,
        cardPromoBadgesContent,
        selectedGateway,
        vsCodeNumber,
    ])

    if (summary.length == 0) return null
    return (
        <table className={styles.table}>
            {summary.map(({ key, ...priceSummarySection }) => (
                <ReceiptSection key={key} {...priceSummarySection} />
            ))}
            <PaymentTerm />
        </table>
    )
}

const PaymentTerm = () => {
    const { suitePayment } = useDeposit()

    if (!isValidPymentTerm(suitePayment) || Amount.isZeroOrNegative(suitePayment.balanceAmount)) return null

    return (
        <tbody>
            <ReceiptRow
                className={styles.paymentTermRow}
                variant="detail"
                label={
                    <Trans>
                        Balance due within{" "}
                        {formatDate(suitePayment.finalDueDate, {
                            month: "short",
                            day: "2-digit",
                            year: "numeric",
                        })}
                    </Trans>
                }
                value={formatPromotionalAmount(O.fromNullable(suitePayment.balanceAmount))}
                isPromotional={false}
            />
        </tbody>
    )
}

export default Receipt
